import { Link } from 'react-router-dom'
import { PublicLayout } from '../components/layout/PublicLayout'

const LAST_UPDATED = 'June 1, 2026'

export default function TermsPage() {
  return (
    <PublicLayout>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 80px' }}>
        <div style={{ marginBottom: 40 }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, margin: '0 0 10px' }}>
            Legal
          </p>
          <h1 style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 10px' }}>
            Terms of Service
          </h1>
          <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0 }}>Last updated {LAST_UPDATED}</p>
        </div>

        <div style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--ink-2)', display: 'flex', flexDirection: 'column', gap: 32 }}>
          <p>
            These Terms of Service ("Terms") govern your use of the Fellowship platform operated by Fellowship ("we", "us", or "our"). By creating an account or using Fellowship, you agree to these Terms.
          </p>

          <Section title="The service">
            <p>Fellowship is a platform that helps Christian churches publish events and helps community members discover and attend those gatherings. Churches subscribe to post events; individual accounts are free.</p>
          </Section>

          <Section title="Accounts">
            <p><strong>Eligibility.</strong> You must be at least 13 years old to create an account. By registering, you represent that you meet this requirement.</p>
            <p><strong>Accuracy.</strong> You agree to provide accurate information when creating your account and to keep it up to date.</p>
            <p><strong>Security.</strong> You are responsible for maintaining the confidentiality of your password and for all activity under your account. Notify us immediately at <a href="mailto:support@davenportministries.com" style={{ color: 'var(--primary)' }}>support@davenportministries.com</a> if you believe your account has been compromised.</p>
            <p><strong>One account per person.</strong> You may not create multiple accounts or share your account credentials with others.</p>
          </Section>

          <Section title="Church subscriptions">
            <p><strong>Subscription plans.</strong> Churches may subscribe to Starter, Community, or Connection plans, billed monthly or annually. Plan features and limits are described on the billing page.</p>
            <p><strong>Free trial.</strong> New church subscriptions include a 14-day free trial. No credit card is required to start a trial. After the trial, a valid payment method is required to continue posting events.</p>
            <p><strong>Billing.</strong> Subscriptions automatically renew unless cancelled before the renewal date. Payments are processed by Stripe. You authorize us to charge your payment method on a recurring basis.</p>
            <p><strong>Cancellation.</strong> You may cancel your subscription at any time from the billing page. Access continues until the end of the current billing period. We do not provide refunds for partial periods.</p>
            <p><strong>Plan limits.</strong> Each plan includes limits on events per month, coordinator seats, and features. Exceeding limits requires upgrading. We reserve the right to adjust plan features with 30 days' notice.</p>
          </Section>

          <Section title="Church responsibilities">
            <p><strong>Accurate information.</strong> Churches are responsible for the accuracy of their profile, address, service times, and event details. Misleading or false information is grounds for account termination.</p>
            <p><strong>Appropriate events.</strong> Events must be genuine fellowship gatherings open to the community. Commercial advertising, recruitment for non-religious organizations, or events that discriminate unlawfully are prohibited.</p>
            <p><strong>Coordinator access.</strong> Church owners are responsible for the actions of coordinators they invite. Coordinators act on behalf of the church.</p>
          </Section>

          <Section title="Event hosts and RSVPs">
            <p><strong>Person-hosted events.</strong> Individual users may host up to 5 events per calendar month on a free account. Events must be genuine community gatherings.</p>
            <p><strong>RSVP data.</strong> When you create an event, you may collect attendee names, email addresses, and dish contributions. This information may only be used to coordinate the event. You may not use RSVP data for unrelated marketing or share it with third parties.</p>
            <p><strong>Address privacy.</strong> For person-hosted events, you may choose to hide your address until RSVPs are approved. You are responsible for approving or declining RSVPs in a timely manner.</p>
            <p><strong>Cancellations.</strong> If you cancel an event, attendees who RSVPed will be notified. Repeated last-minute cancellations may result in account restrictions.</p>
          </Section>

          <Section title="Conduct">
            <p>You agree not to:</p>
            <ul>
              <li>Post false, misleading, or fraudulent event information</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Use Fellowship to collect data for purposes unrelated to event coordination</li>
              <li>Attempt to access other users' accounts or data without authorization</li>
              <li>Scrape, crawl, or systematically extract data from the platform</li>
              <li>Interfere with or disrupt the platform's infrastructure</li>
              <li>Use the platform for any unlawful purpose</li>
            </ul>
          </Section>

          <Section title="Content">
            <p>You retain ownership of content you post (event descriptions, photos, etc.). By posting content, you grant us a non-exclusive, worldwide license to display and distribute that content as part of operating the service. You represent that you have the rights to any content you post and that it does not infringe third-party rights.</p>
            <p>We may remove content that violates these Terms or that we determine, in our sole discretion, is harmful, offensive, or otherwise inappropriate.</p>
          </Section>

          <Section title="Intellectual property">
            <p>The Fellowship platform, including its design, code, and trademarks, is owned by us. These Terms do not grant you any right to use our name, logo, or branding.</p>
          </Section>

          <Section title="Disclaimer of warranties">
            <p>Fellowship is provided "as is" without warranties of any kind, express or implied. We do not warrant that the service will be uninterrupted, error-free, or that events listed are accurate or will occur as described. Church and event information is provided by third-party users; we are not responsible for its accuracy.</p>
          </Section>

          <Section title="Limitation of liability">
            <p>To the maximum extent permitted by law, Fellowship and its officers, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service. Our total liability for any claim shall not exceed the amount you paid us in the 12 months preceding the claim, or $50, whichever is greater.</p>
          </Section>

          <Section title="Indemnification">
            <p>You agree to indemnify and hold harmless Fellowship and its affiliates from any claims, losses, or damages (including legal fees) arising from your use of the service, your content, or your violation of these Terms.</p>
          </Section>

          <Section title="Termination">
            <p>You may delete your account at any time. We may suspend or terminate accounts that violate these Terms, with or without notice. Upon termination, your right to use the service ceases immediately. Provisions that by their nature should survive termination (liability, indemnification, etc.) will do so.</p>
          </Section>

          <Section title="Changes to these terms">
            <p>We may update these Terms from time to time. We will notify registered users by email before material changes take effect. Continued use of the service after changes constitutes acceptance of the updated Terms.</p>
          </Section>

          <Section title="Governing law">
            <p>These Terms are governed by the laws of the United States. Any disputes shall be resolved in the courts of competent jurisdiction.</p>
          </Section>

          <Section title="Contact">
            <p>Questions about these Terms? Email us at <a href="mailto:support@davenportministries.com" style={{ color: 'var(--primary)' }}>support@davenportministries.com</a>.</p>
          </Section>

          <p style={{ fontSize: 13, color: 'var(--muted)', paddingTop: 16, borderTop: '1px solid var(--line-soft)' }}>
            <Link to="/terms" style={{ color: 'var(--primary)' }}>Terms of Service</Link> · <Link to="/privacy" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>
          </p>
        </div>
      </div>
    </PublicLayout>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', margin: '0 0 12px', color: 'var(--ink)' }}>
        {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  )
}
